import { useEffect, useMemo, useRef, useState } from "react";
import DeleteConfirmationForm from "../../components/forms/DeleteConfirmationForm";
import {
  makeDeleteApiCall,
  makeGetApiCall,
  makePostApiCall,
} from "../../shared/makeApiCall";

const DISPLAY_ROLES = new Set(["user", "assistant"]);

function getConversationId(conversation) {
  return conversation?.id || conversation?.uuid || null;
}

function filterDisplayMessages(messages) {
  return (messages || []).filter((message) => DISPLAY_ROLES.has(message.role));
}

function upsertConversationInList(conversations, conversation) {
  const conversationId = getConversationId(conversation);
  const filtered = conversations.filter(
    (item) => getConversationId(item) !== conversationId,
  );
  return [conversation, ...filtered];
}

export function AssistantViewModel() {
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const listEndRef = useRef(null);
  const inputRef = useRef(null);

  const canSend = useMemo(
    () => input.trim().length > 0 && !loading,
    [input, loading],
  );

  const scrollToBottom = () => {
    listEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const focusInput = () => {
    inputRef.current?.focus();
  };

  const loadConversations = async () => {
    const response = await makeGetApiCall("agent/conversations");

    if (response.success) {
      setConversations(
        response.data?.conversations || response.data?.data || [],
      );
    }
  };

  const createConversation = async () => {
    const response = await makePostApiCall("agent/conversations", {});

    if (!response.success) {
      return { success: false, message: response.data?.message };
    }

    const conversation = response.data?.conversation;
    const id = getConversationId(conversation);

    if (!id) {
      return {
        success: false,
        message:
          response.data?.message || "Could not create a new conversation.",
      };
    }

    setActiveConversationId(id);
    setConversations((prev) => upsertConversationInList(prev, conversation));

    return { success: true, uuid: id, conversation };
  };

  const ensureActiveConversation = async () => {
    if (activeConversationId) {
      return { success: true, uuid: activeConversationId };
    }

    return createConversation();
  };

  const startNewConversation = async () => {
    if (loading) return;

    setHistoryLoading(true);
    try {
      const result = await createConversation();

      if (result.success) {
        setMessages([]);
        setInput("");
        focusInput();
        await loadConversations();
      }
    } finally {
      setHistoryLoading(false);
    }
  };

  const openConversation = async (conversationId) => {
    if (!conversationId || loading) return;

    setHistoryLoading(true);
    try {
      const response = await makeGetApiCall(
        `agent/conversations/${conversationId}/messages`,
      );

      if (response.success) {
        setActiveConversationId(
          getConversationId(response.data?.conversation) || conversationId,
        );
        setMessages(filterDisplayMessages(response.data?.messages || []));
        setTimeout(scrollToBottom, 50);
      }
    } finally {
      setHistoryLoading(false);
    }
  };

  const deleteConversation = async (conversationId) => {
    if (!conversationId || loading || historyLoading) return;

    setHistoryLoading(true);
    try {
      const response = await makeDeleteApiCall(
        `agent/conversations/${conversationId}`,
      );

      if (response.success) {
        setEmptyModelOpen({
          open: false,
          childComponent: null,
        });
        setConversations((prev) =>
          prev.filter((item) => getConversationId(item) !== conversationId),
        );

        if (conversationId === activeConversationId) {
          setActiveConversationId(null);
          setMessages([]);
          setInput("");
        }
      } else {
        window.alert(
          response.data?.message || "Could not remove conversation.",
        );
      }
    } finally {
      setHistoryLoading(false);
    }
  };

  const askDeleteConversation = (conversation) => {
    if (loading || historyLoading) return;

    const conversationId = getConversationId(conversation);
    if (!conversationId) return;

    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Remove"
          confirmAction={() => deleteConversation(conversationId)}
          hideReason={true}
          message="Remove this conversation from your history?"
          setOpen={setEmptyModelOpen}
        />
      ),
      open: true,
    });
  };

  const appendMessagesFromResponse = (newMessages) => {
    setMessages((prev) => {
      const withoutPending = prev.filter((message) => !message.pending);
      const existingIds = new Set(
        withoutPending.map((message) => message.id).filter(Boolean),
      );
      const toAdd = filterDisplayMessages(newMessages).filter(
        (message) => !message.id || !existingIds.has(message.id),
      );

      return [...withoutPending, ...toAdd];
    });
  };

  const sendMessage = async () => {
    const question = input.trim();
    if (!question || loading) return;

    setMessages((prev) => [
      ...prev,
      { role: "user", content: question, pending: true },
    ]);
    setInput("");
    setLoading(true);

    try {
      const conversationResult = await ensureActiveConversation();

      if (!conversationResult.success) {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              conversationResult.message ||
              "The AI assistant could not create a conversation.",
            error: true,
          },
        ]);
        return;
      }

      const conversationId = conversationResult.uuid;
      const payload = { message: question };

      if (conversationId) {
        payload.conversation_id = conversationId;
      }

      const response = await makePostApiCall("agent/ask", payload);

      if (response.success) {
        const conversation = response.data?.conversation;

        if (conversation) {
          const id = getConversationId(conversation);
          if (id) {
            setActiveConversationId(id);
          }
          setConversations((prev) => upsertConversationInList(prev, conversation));
        }

        appendMessagesFromResponse([
          response.data?.user_message,
          response.data?.message,
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content:
              response.data?.message ||
              "The AI assistant could not complete the request.",
            error: true,
            errorStatus: response.errorStatus,
          },
        ]);
      }
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "The AI assistant could not be reached.",
          error: true,
        },
      ]);
    } finally {
      setLoading(false);
      setTimeout(scrollToBottom, 50);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  useEffect(() => {
    loadConversations();
  }, []);

  return {
    messages,
    conversations,
    activeConversationId,
    input,
    setInput,
    loading,
    historyLoading,
    canSend,
    sendMessage,
    handleKeyDown,
    listEndRef,
    inputRef,
    openConversation,
    startNewConversation,
    askDeleteConversation,
    emptyModal,
    setEmptyModelOpen,
  };
}
